// src/pages/OrdersList.tsx
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Pagination as MuiPagination,
} from '@mui/material';
// import { useTheme } from '@mui/material/styles';

import { getOrders } from '../../../services/ApiService';
import { GetOrdersFilters } from '../../../models/Filters';
import { useSystemTables } from '../../../hooks/UseSystemTables';
import { GetOrdersResponse } from '../../../models/GetOrdersResponse';
import Order from '../../../models/Order';
import OrderCard from '../../../components/OrderCard/OrderCard';
import Spinner from '../../../components/Spinner/Spinner';
import styles from './OrdersList.module.css';

type Option = {
  value: number | string;
  label: string;
};

const OrdersList: React.FC = () => {
  const { t, i18n } = useTranslation();
  // const theme = useTheme();

  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [filters, setFilters] = useState<GetOrdersFilters>({
    clientInnerId: '',
    startDate: '',
    endDate: '',
    dateSearchType: 'orderCreationTime',
    shift: '',
    orderId: '',
    status: '',
    origin: '',
    isSelfShipping: '',
    transportationCompany: '',
  });
  const [appliedFilters, setAppliedFilters] = useState<GetOrdersFilters>(filters);
  const [page, setPage] = useState<number>(1);

  const { data: systemTablesData, isLoading: tablesLoading } = useSystemTables();

  const { data, isLoading, isError, refetch } = useQuery<GetOrdersResponse>({
    queryKey: ['orders', appliedFilters, page],
    queryFn: () => getOrders({ ...appliedFilters, page }),
  });

  const orders: Order[] = data?.orders || [];
  const totalPages: number = data?.totalPages || 1;
  const currentPage: number = data?.page || 1;

  // Build options from system tables
  const usersOptions: Option[] =
    systemTablesData?.data?.underlingUsers?.map((user) => ({
      value: user.attributes['custom:clientInnerId'],
      label: user.username,
    })) || [];
  usersOptions.unshift({ value: '', label: t('General.Labels.All') });

  const originOptions: Option[] =
    systemTablesData?.data?.warehouseLocations?.map((location) => ({
      value: location._id,
      label: i18n.language === 'he' ? location?.nameHeb : location?.nameEng,
    })) || [];
  originOptions.unshift({ value: '', label: t('General.Labels.All') });

  const statusOptions: Option[] =
    systemTablesData?.data?.orderStatus?.map((status) => ({
      value: status._id,
      label: i18n.language === 'he' ? status.name_hebrew : status.name,
    })) || [];
  statusOptions.unshift({ value: '', label: t('General.Labels.All') });

  const transportationCompanyOptions: Option[] =
    systemTablesData?.data?.transportCompanies?.map((company) => ({
      value: company._id,
      label: company.companyName || company.companyInnerId,
    })) || [];
  transportationCompanyOptions.unshift({ value: '', label: t('General.Labels.All') });

  // Toggle filter visibility
  const handleFilterToggle = () => {
    setIsFilterOpen((prev) => !prev);
  };

  // Handler for text fields
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Updated handler for Select change using Material-UI's SelectChangeEvent
  const handleSelectChange = (event: SelectChangeEvent<string>, child: React.ReactNode) => {
    child;  //"using" unused variable so Amplify won't br angry
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  // Handler for filter form submission
  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setAppliedFilters(filters);
    refetch();
  };

  // Handler for pagination change
  const handlePaginationChange = (event: React.ChangeEvent<unknown>, value: number) => {
    event;  //"using" unused variable so Amplify won't br angry
    setPage(value);
  };

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{t('OrdersList.Title')}</h2>
      <Box className={styles.generalSection}>
        <Box mb={2}>
          <Button variant="contained" onClick={handleFilterToggle}>
            {isFilterOpen ? t('Filters.Close') : t('Filters.Open')}
          </Button>
        </Box>
        {isFilterOpen && (
          <Box component="form" onSubmit={handleFilterSubmit} mb={2}>
            <Grid container spacing={2}>
              {/* Row 1: All filters except dateSearchType, startDate, and endDate */}
              <Grid container item spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="clientInnerId-label">{t('Order.CustomerNum')}</InputLabel>
                    <Select
                      labelId="clientInnerId-label"
                      name="clientInnerId"
                      value={filters.clientInnerId}
                      label={t('Order.CustomerNum')}
                      onChange={handleSelectChange}
                    >
                      {usersOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    name="orderId"
                    label={t('Order.OrderId')}
                    value={filters.orderId}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="status-label">{t('Order.StatusTitle')}</InputLabel>
                    <Select
                      labelId="status-label"
                      name="status"
                      value={filters.status}
                      label={t('Order.StatusTitle')}
                      onChange={handleSelectChange}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="origin-label">{t('Order.Origin')}</InputLabel>
                    <Select
                      labelId="origin-label"
                      name="origin"
                      value={filters.origin}
                      label={t('Order.Origin')}
                      onChange={handleSelectChange}
                    >
                      {originOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="isSelfShipping-label">{t('Order.IsSelfShipping.Title')}</InputLabel>
                    <Select
                      labelId="isSelfShipping-label"
                      name="isSelfShipping"
                      value={filters.isSelfShipping}
                      label={t('Order.IsSelfShipping.Title')}
                      onChange={handleSelectChange}
                    >
                      <MenuItem value="">{t('General.Labels.All')}</MenuItem>
                      <MenuItem value="true">{t('OrdersList.Filter.Labels.isSelfShipping.Yes')}</MenuItem>
                      <MenuItem value="false">{t('OrdersList.Filter.Labels.isSelfShipping.No')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {filters.isSelfShipping === 'false' && (
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel id="transportationCompany-label">
                        {t('Order.TransportationCompany')}
                      </InputLabel>
                      <Select
                        labelId="transportationCompany-label"
                        name="transportationCompany"
                        value={filters.transportationCompany}
                        label={t('Order.TransportationCompany')}
                        onChange={handleSelectChange}
                      >
                        {transportationCompanyOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
              {/* Row 2: Date Search Type and conditional Shift */}
              <Grid container item spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth>
                    <InputLabel id="dateSearchType-label">
                      {t('OrdersList.Filter.Labels.DateSearchType')}
                    </InputLabel>
                    <Select
                      labelId="dateSearchType-label"
                      name="dateSearchType"
                      value={filters.dateSearchType}
                      label={t('OrdersList.Filter.Labels.DateSearchType')}
                      onChange={handleSelectChange}
                    >
                      <MenuItem value="orderCreationTime">
                        {t('OrdersList.Filter.Labels.SearchByOrderCreation')}
                      </MenuItem>
                      <MenuItem value="requestedTime">
                        {t('OrdersList.Filter.Labels.SearchByRequestedDate')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                {filters.dateSearchType === 'requestedTime' && (
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel id="shift-label">{t('OrdersList.Filter.Shift.Label')}</InputLabel>
                      <Select
                        labelId="shift-label"
                        name="shift"
                        value={filters.shift}
                        label={t('OrdersList.Filter.Shift.Label')}
                        onChange={handleSelectChange}
                      >
                        <MenuItem value="">{t('General.Labels.All')}</MenuItem>
                        <MenuItem value="A">{t('OrdersList.Filter.Shift.A')}</MenuItem>
                        <MenuItem value="B">{t('OrdersList.Filter.Shift.B')}</MenuItem>
                        <MenuItem value="C">{t('OrdersList.Filter.Shift.C')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                )}
              </Grid>
              {/* Row 3: Start Date and End Date */}
              <Grid container item spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    name="startDate"
                    label={t('General.Labels.StartDate')}
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={filters.startDate}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    name="endDate"
                    label={t('General.Labels.EndDate')}
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={filters.endDate}
                    onChange={handleInputChange}
                  />
                </Grid>
              </Grid>
              {/* Submit Button */}
              <Grid item xs={12}>
                <Button variant="contained" type="submit">
                  {t('Filters.Apply')}
                </Button>
              </Grid>
            </Grid>
          </Box>
        )}
        <Box className={styles.ordersSection}>
          {isLoading || tablesLoading ? (
            <Spinner />
          ) : isError ? (
            <div>Error loading orders.</div>
          ) : (
            orders.map((order) => <OrderCard key={order.orderId} order={order} />)
          )}
        </Box>
        <Box display="flex" justifyContent="center" mt={2} dir="ltr">
          <MuiPagination
            page={currentPage}
            count={totalPages}
            onChange={handlePaginationChange}
            color="primary"
          />
        </Box>
      </Box>
    </div>
  );
};

export default OrdersList;